import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ltr } from "@/lib/blog/age-bands";
import { primaryPillar } from "@/lib/blog/pillars";
import { articlesPhrase, firstSentence, minutesPhrase } from "@/lib/blog/text";
import { eyebrow, postMeta, secondaryBtn, type BlogPost } from "./shared";

const pillarMeta = (p: BlogPost) =>
  [primaryPillar(p.rakiza), p.reading_min ? minutesPhrase(p.reading_min) : null].filter(Boolean).join(" · ");

export function PathProgress({ label, read, total }: { label: string; read: number; total: number }) {
  return (
    <div className="mt-5">
      <p className="font-display text-[14px] font-bold text-text-secondary">
        مسار {ltr(label)} سنوات · مقروء {read} من {total}
      </p>
      <div
        role="progressbar"
        aria-label={`مسار ${label} سنوات`}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={read}
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-cream-300"
      >
        <div
          className="h-full rounded-full bg-green-500 transition-[width] duration-ds-slow ease-ds"
          style={{ width: `${total ? (read / total) * 100 : 0}%` }}
        />
      </div>
    </div>
  );
}

/** Next unread post in a path, as a white card (used inside articles). */
export function NextInPathCard({ post, index, total }: { post: BlogPost; index: number; total: number }) {
  return (
    <section className="mt-10 rounded-ds-xl border border-hairline-subtle bg-surface p-6 shadow-sm">
      <p className={eyebrow}>المقال الجاي</p>
      <p className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-[34px] font-extrabold leading-none text-amber-800">{index}</span>
        <span className="text-[15px] text-text-secondary">من {total}</span>
      </p>
      {post.tldr_ar && <p className="mt-3 text-[17px] leading-[1.8] text-text-primary">{firstSentence(post.tldr_ar)}</p>}
      <h3 className="mt-2 font-display text-[17px] font-bold text-text-primary">{post.title_ar}</h3>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[14px] text-text-secondary">{pillarMeta(post)}</span>
        <Link to="/blog/$slug" params={{ slug: post.slug }} className="inline-flex min-h-11 items-center font-display text-[15px] font-bold text-amber-800">
          قراءة المقال ←
        </Link>
      </div>
    </section>
  );
}

export function PathView({ path, isRead }: { path: BlogPost[]; isRead: (id: string) => boolean }) {
  const [showAll, setShowAll] = useState(false);
  const read = path.filter((p) => isRead(p.id));
  const unread = path.filter((p) => !isRead(p.id));
  const next = unread[0];
  const after = unread.slice(1);
  const visible = showAll ? after : after.slice(0, 6);
  const indexOf = (p: BlogPost) => path.indexOf(p) + 1;

  return (
    <div className="mt-8 space-y-6">
      {read.length > 0 && (
        <section className="mx-[-8px] rounded-ds-xl bg-green-100 px-5 py-6">
          <p className="font-display text-[13px] font-bold text-green-800">مقروء</p>
          <p className="font-display text-[26px] font-extrabold text-green-800">{read.length}</p>
          <ul className="mt-2 divide-y divide-hairline-subtle">
            {read.map((p) => (
              <li key={p.id}>
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="flex items-center gap-3 py-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-green-800 text-cream-50">
                    <Check className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-[15px] font-bold text-text-primary">{p.title_ar}</span>
                    <span className="block text-[13px] text-green-800">{pillarMeta(p)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {next && (
        <section className="rounded-ds-xl bg-amber-100 px-[22px] py-6">
          <div className="flex items-center gap-3">
            <span className="rounded-ds-pill bg-amber-500 px-3 py-1 font-display text-[13px] font-bold text-brown-700">
              الخطوة الجاية
            </span>
            <span className="flex items-baseline gap-1.5">
              <span className="font-display text-[34px] font-extrabold leading-none text-amber-800">{indexOf(next)}</span>
              <span className="text-[15px] text-text-secondary">من {path.length}</span>
            </span>
          </div>
          {next.tldr_ar && <p className="mt-4 text-[17px] leading-[1.8] text-text-primary">{firstSentence(next.tldr_ar)}</p>}
          <h3 className="mt-2 font-display text-[17px] font-bold text-text-primary">{next.title_ar}</h3>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[14px] text-text-secondary">{pillarMeta(next)}</span>
            <Link to="/blog/$slug" params={{ slug: next.slug }} className="inline-flex min-h-11 items-center font-display text-[15px] font-bold text-amber-800">
              قراءة المقال ←
            </Link>
          </div>
        </section>
      )}

      {after.length > 0 && (
        <section>
          <p className={eyebrow}>بعدها بالترتيب</p>
          <ol className="mt-2 divide-y divide-hairline-subtle">
            {visible.map((p) => (
              <li key={p.id}>
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="flex items-center gap-4 py-4">
                  <span className="w-12 shrink-0 font-display text-[30px] font-extrabold text-amber-800">
                    {String(indexOf(p)).padStart(2, "0")}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-[15px] font-bold text-text-primary">{p.title_ar}</span>
                    <span className="block text-[13px] text-text-secondary">{postMeta(p)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
          {!showAll && after.length > 6 && (
            <button type="button" className={`${secondaryBtn} mt-4 w-full`} onClick={() => setShowAll(true)}>
              عرض {articlesPhrase(after.length - 6)} غيرهم بالمسار
            </button>
          )}
        </section>
      )}
    </div>
  );
}
